import { CSSProperties } from "react";
import { Skeleton } from "@src/lib/ui/Skeleton";
import { cn } from "@src/lib/utils/cn";
import {
  getTableColumnFlexStyle,
  LEADING_ROW_GUTTER,
  OVERFLOW_MENU_COLUMN_WIDTH,
  TableColumnFlexSizing,
} from "@src/lib/ui/tableColumnSizing";

const PADDING_LEFT = 8;

type Column = {
  width?: number;
  minWidth?: number;
  flex?: CSSProperties["flex"];
  /**
   * `horizontalScroll` sizing shared with the header and body cells (see
   * `computeTableColumnSizing`); overrides `width` / `minWidth` / `flex`.
   */
  sizing?: TableColumnFlexSizing;
};

type TableSkeletonProps = {
  columns: Column[];
  rows?: number;
  rowHeight?: number;
  showOverflowColumn?: boolean;
  showCheckboxColumn?: boolean;
  checkboxColumnWidth?: number;
  /**
   * Match a `horizontalScroll` table: no shrinking, column dividers, the
   * "···" column pinned right and the trailing gutter.
   */
  horizontalScroll?: boolean;
  /** Horizontal-scroll trailing gutter after the "···" column. */
  trailingEmptyColumnWidth?: number;
};

function TableSkeleton({
  columns,
  rows = 3,
  rowHeight = 42,
  showOverflowColumn,
  showCheckboxColumn,
  checkboxColumnWidth = 48,
  horizontalScroll = false,
  trailingEmptyColumnWidth = 0,
}: TableSkeletonProps): React.ReactElement {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          data-testid="table-skeleton-row"
          className="flex flex-row self-stretch border-b border-border-primary"
          style={{ minHeight: rowHeight }}
        >
          {showCheckboxColumn ? (
            <div
              className="relative flex shrink-0 items-center justify-center self-stretch"
              style={{
                width: checkboxColumnWidth + PADDING_LEFT,
                minWidth: checkboxColumnWidth + PADDING_LEFT,
              }}
            >
              <Skeleton
                className="h-4"
                style={{ width: checkboxColumnWidth / 2 }}
              />
            </div>
          ) : (
            <div
              aria-hidden="true"
              className="shrink-0 self-stretch"
              style={{ width: LEADING_ROW_GUTTER }}
            />
          )}
          {columns.map((col, j) => (
            <div
              key={j}
              className={cn(
                "relative flex items-center self-stretch",
                horizontalScroll && "border-r border-border-primary/50",
              )}
              style={{
                ...(horizontalScroll && col.sizing
                  ? getTableColumnFlexStyle(col.sizing)
                  : {
                      flex: horizontalScroll ? "0 0 auto" : col.flex ?? 1,
                      width: col.width ? col.width + PADDING_LEFT : undefined,
                      minWidth: col.minWidth
                        ? col.minWidth + PADDING_LEFT
                        : PADDING_LEFT,
                      maxWidth: col.width
                        ? col.width + PADDING_LEFT
                        : undefined,
                    }),
                paddingLeft: PADDING_LEFT,
              }}
            >
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
          {showOverflowColumn && (
            <div
              className={cn(
                "relative flex shrink-0 items-center self-stretch",
                // Pinned like the header and body "···" cells.
                horizontalScroll && "sticky right-0 z-10 ml-auto",
              )}
              style={{
                width: OVERFLOW_MENU_COLUMN_WIDTH + PADDING_LEFT,
                minWidth: OVERFLOW_MENU_COLUMN_WIDTH + PADDING_LEFT,
                maxWidth: OVERFLOW_MENU_COLUMN_WIDTH + PADDING_LEFT,
                paddingLeft: PADDING_LEFT,
              }}
            >
              <Skeleton className="h-4 w-4" />
            </div>
          )}
          {horizontalScroll && (
            <div
              aria-hidden="true"
              className="shrink-0"
              style={{ width: trailingEmptyColumnWidth }}
            />
          )}
        </div>
      ))}
    </>
  );
}

export { TableSkeleton };
